import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  NumberInput,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import {
  IconChartBar,
  IconCoins,
  IconCreditCard,
  IconRefresh,
  IconShoppingBag,
  IconTrendingUp,
  IconTruckDelivery,
  IconWallet,
} from "@tabler/icons-react";
import type { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import DateRangeInput, {
  DateRangeValue,
} from "components/ui/DateRangeInput/DateRangeInput";
import PageContent from "components/layouts/PageContent";
import dayjs from "configs/dayjs";
import {
  RECEIPT_STATUS_COLOR,
  RECEIPT_STATUS_LABEL,
} from "constants/common/receipt";
import {
  SALES_ORDER_STATUS_COLOR,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";
import {
  DashboardParamsType,
  DashboardStatusCounts,
} from "constants/types/dashboard";
import { FC, ReactNode, useMemo, useState } from "react";
import { useDashboardSummary } from "utils/hooks/request/useDashboard";
import { useCustomers } from "utils/hooks/request/usePartners";
import { useListProduct } from "utils/hooks/request/useProducts";
import { useListWarehouse } from "utils/hooks/request/useWarehouses";
import { formatCurrency, formatDate } from "utils/helpers/format";

import classes from "./Dashboard.module.css";

const DASHBOARD_TITLE = "Dashboard";

const getDefaultDateRange = (): DateRangeValue => [
  dayjs().startOf("month").toDate(),
  dayjs().endOf("month").toDate(),
];

const getDefaultParams = (): DashboardParamsType => ({
  startDate: dayjs().startOf("month").unix(),
  endDate: dayjs().endOf("month").unix(),
  lowStockThreshold: 10,
  limit: 5,
});

const compactCurrency = (amount = 0): string =>
  new Intl.NumberFormat("vi-VN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);

const formatNumber = (value = 0): string => value.toLocaleString("vi-VN");

const getStatusRows = (counts?: DashboardStatusCounts) => {
  if (!counts) return [];
  if (Array.isArray(counts)) return counts;
  return Object.entries(counts).map(([status, count]) => ({ status, count }));
};

const statusTotal = (counts?: DashboardStatusCounts) =>
  getStatusRows(counts).reduce((sum, item) => sum + item.count, 0);

interface SectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  rightSection?: ReactNode;
}

const DashboardSection: FC<SectionProps> = ({
  title,
  description,
  children,
  rightSection,
}) => (
  <Paper className={classes.section} withBorder>
    <Group justify="space-between" align="flex-start" mb="md">
      <Box>
        <Text fw={700}>{title}</Text>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
      </Box>
      {rightSection}
    </Group>
    {children}
  </Paper>
);

interface KpiCardProps {
  label: string;
  value: string;
  meta: string;
  icon: ReactNode;
  color: string;
}

const KpiCard: FC<KpiCardProps> = ({ label, value, meta, icon, color }) => (
  <Paper className={classes.kpiCard} withBorder>
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Box>
        <Text size="sm" c="dimmed" fw={600}>
          {label}
        </Text>
        <Text className={classes.kpiValue}>{value}</Text>
      </Box>
      <ThemeIcon color={color} variant="light" size={42} radius="md">
        {icon}
      </ThemeIcon>
    </Group>
    <Text size="xs" c="dimmed" mt="sm">
      {meta}
    </Text>
  </Paper>
);

const Dashboard: FC = () => {
  const theme = useMantineTheme();
  const [dateRange, setDateRange] =
    useState<DateRangeValue>(getDefaultDateRange);
  const [dateError, setDateError] = useState<string | undefined>();
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [lowStockThreshold, setLowStockThreshold] = useState<number | string>(10);
  const [limit, setLimit] = useState<number | string>(5);
  const [appliedParams, setAppliedParams] =
    useState<DashboardParamsType>(getDefaultParams);

  const { dashboard, isLoading, mutate } = useDashboardSummary(appliedParams);
  const { warehouseOptions, isLoading: loadingWarehouses } = useListWarehouse();
  const { customerOptions, isLoading: loadingCustomers } = useCustomers();
  const { productOptions, isLoading: loadingProducts } = useListProduct();

  const salesTrend = dashboard?.charts.salesTrend ?? [];
  const saleOrderStatusRows = getStatusRows(
    dashboard?.charts.saleOrderStatus ?? dashboard?.sales.statusCounts,
  );
  const paymentStatusRows = getStatusRows(
    dashboard?.charts.paymentJournalStatus ?? dashboard?.payments.statusCounts,
  );

  const chartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: "Open Sans, sans-serif",
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 3 },
      grid: { borderColor: theme.colors.gray[2], strokeDashArray: 4 },
      xaxis: {
        categories: salesTrend.map((item) => dayjs(item.day).format("DD/MM")),
        labels: { style: { colors: theme.colors.gray[6] } },
      },
      yaxis: {
        labels: {
          formatter: (value) => compactCurrency(Number(value)),
          style: { colors: theme.colors.gray[6] },
        },
      },
      tooltip: {
        y: { formatter: (value) => formatCurrency(Number(value)) },
      },
      colors: [theme.colors.teal[6], theme.colors.indigo[6], theme.colors.orange[6]],
      legend: { position: "top", horizontalAlign: "left" },
    }),
    [salesTrend, theme],
  );

  const saleStatusOptions = useMemo<ApexOptions>(
    () => ({
      chart: { toolbar: { show: false }, fontFamily: "Open Sans, sans-serif" },
      labels: saleOrderStatusRows.map(
        (item) => SALES_ORDER_STATUS_LABEL[item.status] ?? item.status,
      ),
      legend: { position: "bottom" },
      dataLabels: { enabled: false },
      colors: [
        theme.colors.yellow[5],
        theme.colors.blue[6],
        theme.colors.orange[6],
        theme.colors.green[6],
        theme.colors.red[6],
      ],
    }),
    [saleOrderStatusRows, theme],
  );

  const revenueCollectionRate = dashboard?.kpis.revenue
    ? Math.min((dashboard.kpis.paidAmount / dashboard.kpis.revenue) * 100, 100)
    : 0;

  const handleDateChange = (range: DateRangeValue) => {
    setDateRange(range);
    const [start, end] = range;
    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      setDateError("Ngày bắt đầu phải trước ngày kết thúc");
    } else {
      setDateError(undefined);
    }
  };

  const handleApply = () => {
    const [start, end] = dateRange;
    if (!start || !end) {
      setDateError("Vui lòng chọn khoảng thời gian");
      return;
    }

    setAppliedParams({
      startDate: dayjs(start).startOf("day").unix(),
      endDate: dayjs(end).endOf("day").unix(),
      warehouseId: warehouseId ?? undefined,
      partnerId: partnerId ?? undefined,
      productId: productId ?? undefined,
      lowStockThreshold:
        typeof lowStockThreshold === "number" ? lowStockThreshold : 10,
      limit: typeof limit === "number" ? limit : 5,
    });
  };

  const isInvalid = !dateRange[0] || !dateRange[1] || !!dateError;

  const kpis = dashboard?.kpis;
  const saleTotal = statusTotal(dashboard?.sales.statusCounts);
  const paymentTotal = statusTotal(dashboard?.payments.statusCounts);

  return (
    <Stack gap="md" className={classes.dashboard}>
      <Paper className={classes.hero} withBorder>
        <Flex justify="space-between" align="flex-start" gap="md" wrap="wrap">
          <Box>
            <Badge color="teal" variant="light" mb="xs">
              Quản trị kho & bán hàng
            </Badge>
            <Title order={2}>{DASHBOARD_TITLE}</Title>
            <Text c="dimmed">
              Tổng quan doanh thu, công nợ, phiếu thu và tồn kho theo thời gian
              thực.
            </Text>
          </Box>
          <Tooltip label="Tải lại dữ liệu">
            <ActionIcon
              variant="light"
              color="teal"
              size="lg"
              onClick={() => mutate()}
              loading={isLoading}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Paper>

      <PageContent className={classes.filters}>
        <Flex gap="sm" align="flex-end" wrap="wrap">
          <DateRangeInput
            label="Khoảng thời gian"
            value={dateRange}
            onChange={handleDateChange}
            error={dateError}
            w={280}
          />
          <Select
            label="Kho"
            placeholder="Tất cả kho"
            data={warehouseOptions ?? []}
            value={warehouseId}
            onChange={setWarehouseId}
            disabled={loadingWarehouses}
            clearable
            searchable
            w={220}
          />
          <Select
            label="Khách hàng"
            placeholder="Tất cả khách hàng"
            data={customerOptions ?? []}
            value={partnerId}
            onChange={setPartnerId}
            disabled={loadingCustomers}
            clearable
            searchable
            w={260}
          />
          <Select
            label="Mặt hàng"
            placeholder="Tất cả mặt hàng"
            data={productOptions ?? []}
            value={productId}
            onChange={setProductId}
            disabled={loadingProducts}
            clearable
            searchable
            w={220}
          />
          <NumberInput
            label="Ngưỡng tồn thấp"
            value={lowStockThreshold}
            onChange={setLowStockThreshold}
            min={0}
            w={150}
          />
          <NumberInput
            label="Số dòng"
            value={limit}
            onChange={setLimit}
            min={1}
            max={50}
            w={120}
          />
          <Button onClick={handleApply} disabled={isInvalid}>
            Áp dụng
          </Button>
        </Flex>
      </PageContent>

      <PageContent className={classes.content}>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <KpiCard
              label="Doanh thu"
              value={formatCurrency(kpis?.revenue ?? 0)}
              meta={`${formatNumber(kpis?.saleOrderCount)} đơn bán`}
              icon={<IconTrendingUp size={22} />}
              color="teal"
            />
            <KpiCard
              label="Lợi nhuận gộp"
              value={formatCurrency(kpis?.grossProfit ?? 0)}
              meta={`Thực thu ${formatCurrency(kpis?.realizedProfit ?? 0)}`}
              icon={<IconCoins size={22} />}
              color="green"
            />
            <KpiCard
              label="Đã thu"
              value={formatCurrency(kpis?.paidAmount ?? 0)}
              meta={`Còn phải thu ${formatCurrency(kpis?.receivableAmount ?? 0)}`}
              icon={<IconWallet size={22} />}
              color="indigo"
            />
            <KpiCard
              label="Nhập hàng"
              value={formatCurrency(kpis?.purchaseAmount ?? 0)}
              meta={`${formatNumber(kpis?.purchaseOrderCount)} đơn nhập`}
              icon={<IconTruckDelivery size={22} />}
              color="blue"
            />
            <KpiCard
              label="Công nợ còn lại"
              value={formatCurrency(kpis?.outstandingDebtAmount ?? 0)}
              meta={`${formatNumber(dashboard?.debts.outstandingCount)} công nợ chưa tất toán`}
              icon={<IconCreditCard size={22} />}
              color="red"
            />
            <KpiCard
              label="Phiếu thu chờ duyệt"
              value={formatCurrency(kpis?.pendingPaymentAmount ?? 0)}
              meta={`${formatNumber(kpis?.pendingPaymentCount)} phiếu đang chờ`}
              icon={<IconShoppingBag size={22} />}
              color="yellow"
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 3 }}>
            <Box className={classes.salesTrendSection}>
              <DashboardSection
                title="Xu hướng bán hàng"
                description="Doanh thu, tiền thu và lợi nhuận theo ngày"
                rightSection={
                  <Badge color="teal" variant="light">
                    {formatNumber(salesTrend.length)} ngày
                  </Badge>
                }
              >
                <Box className={classes.wideChart}>
                  <ReactApexChart
                    options={chartOptions}
                    series={[
                      {
                        name: "Doanh thu",
                        data: salesTrend.map((item) => item.revenue),
                      },
                      {
                        name: "Đã thu",
                        data: salesTrend.map((item) => item.paidAmount),
                      },
                      {
                        name: "Lợi nhuận gộp",
                        data: salesTrend.map((item) => item.grossProfit),
                      },
                    ]}
                    type="area"
                    height={330}
                  />
                </Box>
              </DashboardSection>
            </Box>

            <DashboardSection
              title="Trạng thái đơn bán"
              description={`${formatNumber(saleTotal)} đơn trong kỳ`}
              rightSection={<IconChartBar size={20} color={theme.colors.gray[6]} />}
            >
              <ReactApexChart
                options={saleStatusOptions}
                series={saleOrderStatusRows.map((item) => item.count)}
                type="donut"
                height={260}
              />
            </DashboardSection>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <DashboardSection
              title="Tỷ lệ thu tiền"
              description="So sánh tiền đã thu với doanh thu đơn bán"
            >
              <Text className={classes.bigMetric}>
                {revenueCollectionRate.toFixed(1)}%
              </Text>
              <Progress
                value={revenueCollectionRate}
                color="teal"
                size="lg"
                radius="xl"
                mt="xs"
              />
              <SimpleGrid cols={2} mt="md">
                <Box>
                  <Text size="sm" c="dimmed">
                    Đã thu
                  </Text>
                  <Text fw={700}>{formatCurrency(kpis?.paidAmount ?? 0)}</Text>
                </Box>
                <Box>
                  <Text size="sm" c="dimmed">
                    Còn phải thu
                  </Text>
                  <Text fw={700}>{formatCurrency(kpis?.receivableAmount ?? 0)}</Text>
                </Box>
              </SimpleGrid>
            </DashboardSection>

            <DashboardSection
              title="Trạng thái phiếu thu"
              description={`${formatNumber(paymentTotal)} phiếu thu trong kỳ`}
            >
              <Stack gap="sm">
                {paymentStatusRows.map((item) => (
                  <Group key={item.status} justify="space-between">
                    <Badge
                      color={RECEIPT_STATUS_COLOR[item.status] ?? "gray"}
                      variant="light"
                    >
                      {RECEIPT_STATUS_LABEL[item.status] ?? item.status}
                    </Badge>
                    <Text fw={700}>{formatNumber(item.count)}</Text>
                  </Group>
                ))}
              </Stack>
            </DashboardSection>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, xl: 2 }}>
            <DashboardSection title="Khách hàng doanh thu cao nhất">
              <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Khách hàng</Table.Th>
                    <Table.Th ta="right">Doanh thu</Table.Th>
                    <Table.Th ta="right">Đã thu</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(dashboard?.topCustomersByRevenue ?? []).map((item) => (
                    <Table.Tr key={item.partnerId ?? item.partnerCode}>
                      <Table.Td>
                        <Text fw={600}>
                          {item.partnerName ?? item.customerName ?? "Không rõ"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {item.partnerCode ?? item.customerCode ?? "—"} •{" "}
                          {formatNumber(item.orderCount)} đơn
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right" fw={700}>
                        {formatCurrency(item.totalRevenue ?? item.revenue ?? 0)}
                      </Table.Td>
                      <Table.Td ta="right">
                        {formatCurrency(item.paidAmount ?? 0)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </DashboardSection>

            <DashboardSection title="Khách hàng nợ nhiều nhất">
              <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Khách hàng</Table.Th>
                    <Table.Th ta="right">Còn nợ</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(dashboard?.debts.topCustomersByDebt ?? []).map((item) => (
                    <Table.Tr key={item.partnerId ?? item.partnerCode}>
                      <Table.Td>
                        <Text fw={600}>
                          {item.partnerName ?? item.customerName ?? "Không rõ"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {item.partnerCode ?? item.customerCode ?? "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right" fw={700}>
                        {formatCurrency(
                          item.debtAmount ??
                            item.outstandingAmount ??
                            item.remainingAmount ??
                            0,
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </DashboardSection>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1 }}>
            <DashboardSection title="Đơn bán gần đây">
              <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Đơn bán</Table.Th>
                    <Table.Th>Khách hàng</Table.Th>
                    <Table.Th ta="right">Giá trị</Table.Th>
                    <Table.Th>Trạng thái</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(dashboard?.recentSaleOrders ?? []).map((item) => (
                    <Table.Tr key={item.id ?? item.code ?? item.orderCode}>
                      <Table.Td>
                        <Text fw={600}>{item.code ?? item.orderCode ?? "—"}</Text>
                        <Text size="xs" c="dimmed">
                          {formatDate(item.createdAt ?? 0)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{item.partner?.name ?? item.partnerName ?? item.customerName ?? "—"}</Text>
                        <Text size="xs" c="dimmed">
                          {item.warehouse?.name ?? ""}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right" fw={700}>
                        {formatCurrency(item.totalAmount ?? 0)}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={SALES_ORDER_STATUS_COLOR[item.status ?? ""] ?? "gray"}
                          variant="light"
                        >
                          {SALES_ORDER_STATUS_LABEL[item.status ?? ""] ??
                            item.status ??
                            "—"}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </DashboardSection>
          </SimpleGrid>
        </Stack>
      </PageContent>
    </Stack>
  );
};

export default Dashboard;
